<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import FieldCard from './FieldCard.vue';
import GroupFieldCard from './GroupFieldCard.vue';

const props = defineProps({
  page: { type: Object, required: true },
  pageIndex: { type: Number, required: true },
  totalPages: { type: Number, required: true },
  // Full ordered page list (id + title), used by FieldCard to offer branch targets.
  pages: { type: Array, default: () => [] },
  // Flat field list (value + label) used by FieldCard for conditional rule pickers.
  availableFields: { type: Array, default: () => [] },
  usedMappedAttributes: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'updatePage',
  'deletePage',
  'updateField',
  'deleteField',
  'dropField',
  'dragstartField',
]);

const { t } = useI18n();

const showConfig = ref(false);
const isDragOver = ref(false);

function toggleConfig() {
  showConfig.value = !showConfig.value;
}

function updateTitle(e) {
  emit('updatePage', { ...props.page, title: e.target.value });
}

function updateDescription(e) {
  emit('updatePage', { ...props.page, description: e.target.value });
}

function updateNavLabel(e) {
  emit('updatePage', { ...props.page, nav_label: e.target.value });
}

function handleFieldUpdate(fieldIndex, updatedField) {
  emit('updateField', props.pageIndex, fieldIndex, updatedField);
}

function handleFieldDelete(fieldIndex) {
  emit('deleteField', props.pageIndex, fieldIndex);
}

function handleDragOver(e) {
  e.preventDefault();
  e.dataTransfer.dropEffect = 'copy';
  isDragOver.value = true;
}

function handleDragLeave() {
  isDragOver.value = false;
}

function handleDrop(e) {
  e.preventDefault();
  isDragOver.value = false;

  const fieldsBundle = e.dataTransfer.getData('fields-bundle');
  const fieldType = e.dataTransfer.getData('field-type');
  const fieldData = e.dataTransfer.getData('field-data');

  if (fieldsBundle) {
    emit('dropField', props.pageIndex, null, JSON.parse(fieldsBundle));
  } else if (fieldData) {
    emit('dropField', props.pageIndex, null, JSON.parse(fieldData));
  } else if (fieldType) {
    emit('dropField', props.pageIndex, fieldType);
  }
}

function handleFieldDragStart(fieldIndex, e) {
  const field = props.page.fields[fieldIndex];
  e.dataTransfer.setData(
    'field-data',
    JSON.stringify({
      ...field,
      sourcePageIndex: props.pageIndex,
      sourceFieldIndex: fieldIndex,
    })
  );
  e.dataTransfer.effectAllowed = 'move';
  emit('dragstartField', props.pageIndex, fieldIndex);
}
</script>

<template>
  <div
    class="rounded-xl border border-n-weak bg-n-slate-2 mb-4 overflow-hidden transition-shadow hover:shadow-[0_0_0_1px_rgba(39,129,246,0.3)]"
  >
    <!-- Page Header -->
    <div class="border-b border-n-weak bg-n-alpha-1">
      <div class="px-4 py-3 flex items-center gap-3">
        <!-- Drag handle -->
        <div
          class="text-n-slate-8 hover:text-n-slate-10 cursor-grab active:cursor-grabbing"
        >
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <circle cx="9" cy="6" r="1.5" />
            <circle cx="15" cy="6" r="1.5" />
            <circle cx="9" cy="12" r="1.5" />
            <circle cx="15" cy="12" r="1.5" />
            <circle cx="9" cy="18" r="1.5" />
            <circle cx="15" cy="18" r="1.5" />
          </svg>
        </div>

        <div class="flex-1 min-w-0">
          <span class="text-sm font-semibold text-n-slate-12">
            {{ page.title || t('FORM_BUILDER.PAGE.UNTITLED') }}
          </span>
          <span v-if="page.description" class="text-xs text-n-slate-10 ml-2">
            {{ page.description }}
          </span>
        </div>

        <span
          class="text-[10px] text-n-slate-9 font-medium bg-n-alpha-2 px-1.5 py-0.5 rounded shrink-0"
        >
          {{
            t('FORM_BUILDER.PAGE.PAGE_N_OF_M', {
              n: pageIndex + 1,
              m: totalPages,
            })
          }}
        </span>

        <button
          class="w-6 h-6 rounded flex items-center justify-center text-n-slate-9 hover:text-n-slate-12 hover:bg-n-alpha-2"
          :title="t('FORM_BUILDER.PAGE.SETTINGS')"
          @click="toggleConfig"
        >
          <svg
            class="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </button>

        <button
          class="text-n-slate-8 hover:text-red-400 transition-colors"
          @click="$emit('deletePage', pageIndex)"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        </button>
      </div>

      <!-- Page Config Panel -->
      <div v-if="showConfig" class="px-4 pb-3">
        <div class="grid grid-cols-2 gap-3 pt-2 border-t border-n-weak">
          <div class="flex flex-col gap-1 col-span-2">
            <label
              class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
            >
              {{ t('FORM_BUILDER.PAGE.TITLE_LABEL') }}
            </label>
            <input
              type="text"
              :value="page.title"
              class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm font-medium text-n-slate-12"
              @input="updateTitle"
            />
          </div>
          <div class="flex flex-col gap-1 col-span-2">
            <label
              class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
            >
              {{ t('FORM_BUILDER.PAGE.DESCRIPTION_LABEL') }}
            </label>
            <textarea
              rows="2"
              :value="page.description"
              :placeholder="t('FORM_BUILDER.PAGE.DESCRIPTION_PLACEHOLDER')"
              class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12 placeholder-n-slate-9"
              @input="updateDescription"
            />
          </div>
          <div class="flex flex-col gap-1">
            <label
              class="text-[10px] font-medium text-n-slate-10 uppercase tracking-wider"
            >
              {{ t('FORM_BUILDER.PAGE.NAV_LABEL') }}
            </label>
            <input
              type="text"
              :value="page.nav_label || ''"
              :placeholder="t('FORM_BUILDER.PAGE.NAV_LABEL_PLACEHOLDER')"
              class="w-full px-2.5 py-2 rounded-lg bg-n-alpha-1 border border-n-weak text-sm text-n-slate-12"
              @input="updateNavLabel"
            />
            <span class="text-[10px] text-n-slate-9">
              {{ t('FORM_BUILDER.PAGE.NAV_LABEL_HINT') }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Fields Drop Zone -->
    <div
      class="p-3 space-y-2 min-h-[80px] border-2 border-dashed transition-colors"
      :class="
        isDragOver ? 'border-woot-500 bg-n-blue-2/30' : 'border-transparent'
      "
      @drop="handleDrop"
      @dragover="handleDragOver"
      @dragleave="handleDragLeave"
    >
      <component
        :is="field.field_type === 'group' ? GroupFieldCard : FieldCard"
        v-for="(field, fieldIndex) in page.fields"
        :key="field.id || fieldIndex"
        :field="field"
        :index="fieldIndex"
        :pages="pages"
        :page-index="pageIndex"
        :available-fields="availableFields"
        :used-mapped-attributes="usedMappedAttributes"
        @update="updated => handleFieldUpdate(fieldIndex, updated)"
        @delete="handleFieldDelete(fieldIndex)"
        @dragstart="e => handleFieldDragStart(fieldIndex, e)"
      />

      <div
        v-if="!page.fields || page.fields.length === 0"
        class="flex items-center justify-center py-8 text-n-slate-9 text-sm"
      >
        {{ t('FORM_BUILDER.PAGE.DROP_FIELDS_HERE') }}
      </div>
    </div>
  </div>
</template>
